import {
  BookOpen,
  Box,
  Briefcase,
  Building2,
  Clock,
  Code,
  Compass,
  FileText,
  Flag,
  Folder,
  Globe,
  Heart,
  Lightbulb,
  Lock,
  Megaphone,
  Palette,
  Play,
  Rocket,
  Star,
  Target,
  Users,
  Zap,
} from "lucide-react";
import type { ReactElement } from "react";

type IconElement = ReactElement<{ size?: number }>;

/** Space `icon` names (stored free-form by the backend) → lucide icons. Unknown names fall back to a box. */
const SPACE_ICONS: Record<string, (size: number) => IconElement> = {
  box: (size) => <Box size={size} />,
  "book-open": (size) => <BookOpen size={size} />,
  briefcase: (size) => <Briefcase size={size} />,
  building: (size) => <Building2 size={size} />,
  clock: (size) => <Clock size={size} />,
  code: (size) => <Code size={size} />,
  compass: (size) => <Compass size={size} />,
  "file-text": (size) => <FileText size={size} />,
  flag: (size) => <Flag size={size} />,
  folder: (size) => <Folder size={size} />,
  globe: (size) => <Globe size={size} />,
  heart: (size) => <Heart size={size} />,
  lightbulb: (size) => <Lightbulb size={size} />,
  lock: (size) => <Lock size={size} />,
  megaphone: (size) => <Megaphone size={size} />,
  palette: (size) => <Palette size={size} />,
  play: (size) => <Play size={size} />,
  rocket: (size) => <Rocket size={size} />,
  star: (size) => <Star size={size} />,
  target: (size) => <Target size={size} />,
  users: (size) => <Users size={size} />,
  zap: (size) => <Zap size={size} />,
};

/** Every icon a space can use, in the order the icon picker shows them. */
export const SPACE_ICON_NAMES = Object.keys(SPACE_ICONS);

export function spaceIcon(name: string | undefined, size = 14): IconElement {
  return (SPACE_ICONS[name ?? ""] ?? SPACE_ICONS.box)(size);
}

/**
 * The design system tints space icons by hue (SpaceIcon `--sp-h`), so the
 * backend's hex color is reduced to its hue. Greys (no hue) get the default.
 */
export function hueOf(hex: string | undefined, fallback = 275): number {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex ?? "");
  if (!match) return fallback;
  const n = Number.parseInt(match[1], 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const delta = max - Math.min(r, g, b);
  if (delta === 0) return fallback;
  let h: number;
  if (max === r) h = ((g - b) / delta) % 6;
  else if (max === g) h = (b - r) / delta + 2;
  else h = (r - g) / delta + 4;
  return Math.round((h * 60 + 360) % 360);
}
